import React, { useRef, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import FormProvider from "@/core/providers/FormProvider";
import { useCaptcha } from "@/core/providers/CaptchaProvider";
import {ButtonPill} from "@/base";
import FormApi from "@apis/FormApi";

const USE_CAPTCHA = !!process.env.RECAPTCHA_KEY;

function Form({ name, children, onSubmit, onChange, onSuccess, customSubmit }) {

    // create initial state object from child fields
    const childrenToState = (children) => {
        return React.Children.map(children, (child) => {
            return child.props
                ? React.Children.count(child.props.children)
                    ? // recurse if there are more children
                    childrenToState(child.props.children)
                    : // checkboxes default to empty array, everything else is empty string
                    { [child.props.name]: child.props.type === "checkbox" ? [] : "" }
                : // skip this child if there are no props
                {};
        }).reduce((acc, cur) => ({ ...acc, ...cur }));
    };

    // `processor` is used by Atom Form Handler to identify the form for validation
    const initialState = { ...childrenToState(children), processor: name };

    // place for the form values, since we need to hold them briefly while captcha validates.
    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [captchaCode, setCaptchaCode] = useState(false);

    // Remember to reload the recaptcha code when the form loads.
    const myInvisibleCaptcha = useRef();

    // get captcha details.
    const { siteKey, isLoaded: captchaIsLoaded } = useCaptcha();

    const onValidateCaptcha = async (code) => {
        await setCaptchaCode(code);
    };

    const resetCaptcha = async () => {
        await setCaptchaCode("")
    }

    const preSubmitCheck = async (fv) => {
        await setFormErrors({}); //clear errors

        // set the completed form values internally,
        // this is to support the fact that you cannot pass state data properly through google's recaptcha API
        // instead, we keep track of the "complete" form here, and an onComplete in grecaptcha calls
        // the next step, which picks it up for use.
        await setFormValues(fv);

        if (USE_CAPTCHA) {
            await captchaIsLoaded;
            if (captchaCode.length > 0) {
                // If we already have a code, then we can assume we already passed captcha.
                // forged codes wont pass server validation.
                await postToForm({ ...fv, _captcha: captchaCode });
            } else {
                // if we dont have a code, execute the captcha, which validates the submission.
                // the captcha has on-complete code to call the other half of the process.
                try {
                    window.grecaptcha.execute();
                } catch (e) {
                    console.log("Issue rendering captcha", e);
                }
            }

        } else {
            await postToForm(fv);
        }
    };

    const postToForm = useCallback(async (data) => {
        try {
            const result = await onSubmit(data);
            onSuccess && onSuccess(result);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'generate_lead',
                'form': `${name}`
            });

            if(name === 'lac_request_quote'){
                window.dataLayer.push({
                    'event': 'pinterest_generate_lead'
                });
            }

            // Same as below, this next line is potentially an anti-pattern, the idea here is to scroll to the top
            // of the page if any form submitted successfully. I believe this is always desired. (if not lets find out when)
            // Fixes "thank you" states for forms not actually being considered new pages, and so not
            // scrolling to top naturally.
            document.body.scrollIntoView({behavior: "instant"}) // instant here as users will associate it with a "new page"
        } catch (xhr) {
            // okay, we failed to post to the form, lets try to get some errors out.
            try {
                await setFormErrors(xhr.response.data.errors || {}); // pass errors straight through
                // This is potentially an anti-pattern, but the objective is to scroll
                // to the parent of the first activate FieldError below the form. I cannot find a good way to handle this
                // with refs considering the nature of how these forms are put together. I am probably missing a technique for this.
                // but so far this works great?
                document.querySelector("[data-field-error-name]").parentNode.scrollIntoView({ behavior: "smooth" })
            } catch (e) {
                await setFormErrors({});
            }
        }
    }, [onSubmit,onSuccess,setFormErrors, name])

    // Run Once
    useEffect(() => {
        (async () => {
            if (USE_CAPTCHA) {
                await captchaIsLoaded;
                try {
                    window.grecaptcha.render(myInvisibleCaptcha.current, {
                        callback: onValidateCaptcha,
                        "expired-callback": resetCaptcha
                    });
                } catch (e) {
                    console.log("Issue rendering captcha", e);
                }
            }
        })()
    }, [captchaIsLoaded]);

    useEffect(() => {
        (async () => {
            if (!captchaCode || captchaCode.length === 0) return;
            postToForm({ ...formValues, _captcha: captchaCode });
        })()
    }, [captchaCode,postToForm,formValues]);

    const handleChange = useCallback(formData => {
        onChange && onChange(formData);
    }, [onChange])

    const handleFieldChange = (delta) => {
        // whenever a field changes, remove the error message it may have.
        setFormErrors({ ...formErrors, [delta.name]: null });
    }

    return (
        <FormProvider initialState={initialState}
            onSubmit={preSubmitCheck}
            onChange={handleChange}
            onFieldChange={handleFieldChange}
            formErrors={formErrors}>
            {children}
            <div
                ref={myInvisibleCaptcha}
                id={"grecaptcha"}
                className="g-recaptcha"
                data-sitekey={siteKey}
                data-size="invisible"
            ></div>

            {!customSubmit && (
            <div className="mx-auto" css={css(`text-align: center`)}>
                <ButtonPill type="submit">Submit</ButtonPill>
            </div>
            )}
        </FormProvider>
    );
}

Form.defaultProps = {
    onSubmit: async (formState) => {
        return new FormApi().submit(formState)
    },
};

Form.propTypes = {
    children: PropTypes.node,
    initialState: PropTypes.object,
    onSubmit: PropTypes.func,
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    onSuccess: PropTypes.func,
    customSubmit: PropTypes.bool
};

export default Form;
